import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { WebSocketMessage } from '../types/models';
import { webSocketService } from '../services/websocket';

interface Notification {
  id: string;
  type: 'success' | 'error' | 'info';
  message: string;
  timeout: number;
}

const NotificationSystem: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { isConnected } = useAppContext();

  useEffect(() => {
    const handleWebSocketMessage = (message: WebSocketMessage) => {
      if (message.event === 'search_complete') {
        addNotification({
          type: 'success',
          message: `Поиск для позиции ${message.spn_id} завершен успешно`,
          timeout: 5000,
        });
      } else if (message.event === 'search_error') {
        addNotification({
          type: 'error',
          message: `Ошибка поиска для позиции ${message.spn_id}: ${message.error || 'Неизвестная ошибка'}`,
          timeout: 8000,
        });
      }
    };

    webSocketService.addMessageHandler(handleWebSocketMessage);

    return () => {
      webSocketService.removeMessageHandler(handleWebSocketMessage);
    };
  }, []);

  // Show connection status notification when WebSocket connection changes
  useEffect(() => {
    if (isConnected) {
      addNotification({
        type: 'info',
        message: 'Соединение с сервером установлено',
        timeout: 3000,
      });
    }
  }, [isConnected]);

  const addNotification = ({ type, message, timeout }: Omit<Notification, 'id'>) => {
    const id = Date.now().toString();
    const newNotification: Notification = {
      id,
      type,
      message,
      timeout,
    };

    setNotifications((prev) => [...prev, newNotification]);

    // Auto-remove notification after timeout
    setTimeout(() => {
      removeNotification(id);
    }, timeout);
  };

  const removeNotification = (id: string) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== id));
  };

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className={`p-3 rounded-lg shadow-lg flex justify-between items-center min-w-[300px] max-w-md animate-fade-in ${
            notification.type === 'success'
              ? 'bg-green-100 text-green-800'
              : notification.type === 'error'
              ? 'bg-red-100 text-red-800'
              : 'bg-blue-100 text-blue-800'
          }`}
        >
          <div>{notification.message}</div>
          <button
            onClick={() => removeNotification(notification.id)}
            className="ml-2 text-gray-500 hover:text-gray-700"
          >
            &times;
          </button>
        </div>
      ))}
    </div>
  );
};

export default NotificationSystem;